import { useState } from 'react';

interface AlertModal {
  isOpen: boolean;
  type: 'info' | 'warning' | 'error' | 'success';
  message: string;
  title?: string;
}

interface ConfirmModal {
  isOpen: boolean;
  type: 'info' | 'warning' | 'danger' | 'success';
  message: string;
  details?: string;
  title?: string;
  onConfirm?: () => void;
}

interface PromptModal {
  isOpen: boolean;
  isMinimized: boolean;
  isLoading?: boolean;
  message: string;
  title?: string;
  placeholder?: string;
  defaultValue?: string;
  multiline?: boolean;
  richText?: boolean;
  onConfirm?: (value: string) => void;
}

interface NavigationModal {
  isOpen: boolean;
  onConfirm?: () => void;
}

interface SolveModal {
  isOpen: boolean;
  isMinimized: boolean;
  loading?: boolean;
}

interface AssignModal {
  isOpen: boolean;
  isMinimized: boolean;
  isLoading: boolean;
}

interface RemandModal {
  isOpen: boolean;
  isMinimized: boolean;
  isLoading: boolean;
}

interface CommentAttachModal {
  isOpen: boolean;
  isMinimized: boolean;
  isLoading: boolean;
}

interface AddObserversModal {
  isOpen: boolean;
  isMinimized: boolean;
  isLoading: boolean;
}

interface RemoveAssignedModal {
  isOpen: boolean;
  isMinimized: boolean;
  isLoading: boolean;
}

interface FinalizeModal {
  isOpen: boolean;
  isMinimized: boolean;
  isLoading: boolean;
}

export const useDemandModals = () => {
  // Estados dos modais
  const [alertModal, setAlertModal] = useState<AlertModal>({ 
    isOpen: false, 
    type: 'info', 
    message: '' 
  });
  
  const [confirmModal, setConfirmModal] = useState<ConfirmModal>({ 
    isOpen: false, 
    type: 'info', 
    message: '' 
  });
  
  const [promptModal, setPromptModal] = useState<PromptModal>({ 
    isOpen: false, 
    isMinimized: false,
    isLoading: false,
    message: '' 
  });

  const [navigationModal, setNavigationModal] = useState<NavigationModal>({ 
    isOpen: false 
  });

  const [solveModal, setSolveModal] = useState<SolveModal>({ 
    isOpen: false, 
    isMinimized: false 
  });

  const [assignModal, setAssignModal] = useState<AssignModal>({ 
    isOpen: false, 
    isMinimized: false, 
    isLoading: false 
  });

  const [remandModal, setRemandModal] = useState<RemandModal>({ 
    isOpen: false, 
    isMinimized: false, 
    isLoading: false 
  });

  const [commentAttachModal, setCommentAttachModal] = useState<CommentAttachModal>({ 
    isOpen: false, 
    isMinimized: false, 
    isLoading: false 
  });

  const [addObserversModal, setAddObserversModal] = useState<AddObserversModal>({ 
    isOpen: false, 
    isMinimized: false, 
    isLoading: false 
  });

  const [removeAssignedModal, setRemoveAssignedModal] = useState<RemoveAssignedModal>({ 
    isOpen: false, 
    isMinimized: false, 
    isLoading: false 
  });

  const [finalizeModal, setFinalizeModal] = useState<FinalizeModal>({ 
    isOpen: false, 
    isMinimized: false, 
    isLoading: false 
  });

  // Funções auxiliares para modais
  const showAlert = (
    type: 'info' | 'warning' | 'error' | 'success', 
    message: string, 
    title?: string
  ) => {
    setAlertModal({ isOpen: true, type, message, ...(title ? { title } : {}) });
  };

  const showConfirm = (
    type: 'info' | 'warning' | 'danger' | 'success', 
    message: string, 
    onConfirm: () => void,
    details?: string,
    title?: string
  ) => {
    setConfirmModal({ 
      isOpen: true, 
      type, 
      message, 
      onConfirm,
      ...(details ? { details } : {}),
      ...(title ? { title } : {}) 
    });
  };

  const showPrompt = (
    message: string,
    onConfirm: (value: string) => void,
    options?: {
      title?: string;
      placeholder?: string;
      defaultValue?: string;
      multiline?: boolean;
      richText?: boolean;
    }
  ) => {
    const modalData = { 
      isOpen: true,
      isMinimized: false,
      message, 
      onConfirm,
      ...(options?.title ? { title: options.title } : {}),
      ...(options?.placeholder ? { placeholder: options.placeholder } : {}),
      ...(options?.defaultValue ? { defaultValue: options.defaultValue } : {}),
      ...(options?.multiline !== undefined ? { multiline: options.multiline } : {}),
      ...(options?.richText !== undefined ? { richText: options.richText } : {})
    };
    setPromptModal(modalData);
  };

  // Funções para restaurar modais minimizados
  const restorePromptModal = () => {
    setPromptModal(prev => ({ ...prev, isOpen: true, isMinimized: false }));
  };

  const restoreAssignModal = () => {
    setAssignModal(prev => ({ ...prev, isOpen: true, isMinimized: false }));
  };

  const restoreRemandModal = () => {
    setRemandModal(prev => ({ ...prev, isOpen: true, isMinimized: false }));
  };

  const restoreSolveModal = () => {
    setSolveModal(prev => ({ ...prev, isOpen: true, isMinimized: false }));
  };

  const restoreCommentAttachModal = () => {
    setCommentAttachModal(prev => ({ ...prev, isOpen: true, isMinimized: false }));
  };

  const restoreAddObserversModal = () => {
    setAddObserversModal(prev => ({ ...prev, isOpen: true, isMinimized: false }));
  };

  const restoreRemoveAssignedModal = () => {
    setRemoveAssignedModal(prev => ({ ...prev, isOpen: true, isMinimized: false }));
  };

  const restoreFinalizeModal = () => {
    setFinalizeModal(prev => ({ ...prev, isOpen: true, isMinimized: false }));
  };

  return {
    // Estados dos modais
    alertModal,
    confirmModal,
    promptModal,
    navigationModal,
    solveModal,
    assignModal,
    remandModal,
    commentAttachModal,
    addObserversModal,
    removeAssignedModal,
    finalizeModal,
    
    // Setters dos modais
    setAlertModal,
    setConfirmModal,
    setPromptModal,
    setNavigationModal,
    setSolveModal,
    setAssignModal,
    setRemandModal,
    setCommentAttachModal,
    setAddObserversModal,
    setRemoveAssignedModal,
    setFinalizeModal,
    
    // Funções utilitárias
    showAlert,
    showConfirm,
    showPrompt,
    
    // Funções de restauração
    restorePromptModal,
    restoreAssignModal,
    restoreRemandModal,
    restoreSolveModal,
    restoreCommentAttachModal,
    restoreAddObserversModal,
    restoreRemoveAssignedModal,
    restoreFinalizeModal
  };
};